import React, { JSX } from "react";
import { Route } from "react-router";
import queryString from "query-string";
import { Box } from "@mui/material";

import Can from "../Policy/Can";
import Assessment from "./index";
import OpdRequests from "./OpdRequest";
import { getServices } from "../../actions";
import AssessmentList from "./AssessmentList";
import PrescribedLabs from "./PrescribedLabs";
import DentalLabOrders from "./DentalLabOrder";
import { t } from "../../components/translate";
import { MultipleHeader } from "../../components/List";
import { getAssessmentById } from "../../api/assessment";
import GlassOrderList from "./GlassOrder/GlassOrderList";
import ReportsPanel from "../Reports/Panel/ReportsPanel";
import { notificationAdd } from "../../actions/notification";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { errorFetchMessage, MODULE } from "../../helpers/messages";
import { OpdSettingsPanel } from "../ResourceCentre/ModuleSettings/OpdSettings/OpdSettingsPanel";
import { Assessment as IAssessment } from "../../interfaces/AssessmentInterfaces";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  location: Record<string, any>;
}

function AssessmentHome({ location, match }: Props): JSX.Element {
  const { id } = queryString.parse(location.search);

  // hooks and selectors
  const dispatch = useAppDispatch();
  const services = useAppSelector((state) => state.services.collection);
  const resourceCentre = useCurrentResourceCentre();

  // local states
  const [assessmentCopy, setAssessmentCopy] = React.useState<null | IAssessment>(null);

  const opdOrderFormsSettings = resourceCentre?.settings?.opdOrderFormsSettings;
  const showLabOrders = opdOrderFormsSettings?.labOrders?.show ?? true;
  const showDentalOrders = opdOrderFormsSettings?.dentalOrders?.show ?? true;
  const showGlassOrders = opdOrderFormsSettings?.glassOrders?.show ?? true;

  // constants
  const headers = [
    { key: "new", title: `${t("Newprescription")}`, goto: "/assessment/new" },
    {
      key: "assessments",
      title: `${t("Prevprescription")}`,
      goto: "/assessment/assessments"
    },
    {
      key: "labTest",
      title: "Prescribed Lab Test",
      goto: "/assessment/labTest",
      badge: 0
    },
    ...(showLabOrders
      ? [
          {
            key: "labOrderForms",
            title: "Order Forms for Laboratory",
            goto: "/assessment/labOrderForms",
            badge: 0
          }
        ]
      : []),
    ...(showDentalOrders
      ? [
          {
            key: "dentalLabOrders",
            title: "Dental Lab Orders",
            goto: "/assessment/dentalLabOrders",
            badge: 0
          }
        ]
      : []),
    ...(showGlassOrders
      ? [
          {
            key: "glassOrders",
            title: "Glass Orders",
            goto: "/assessment/glassOrders",
            badge: 0
          }
        ]
      : [])
  ];

  const headersInfo = {
    headers,
    url: location.pathname
  };

  // effects
  React.useEffect(() => {
    if (!assessmentCopy && id) {
      (async () => {
        try {
          const assessment = await getAssessmentById(Number(id));
          setAssessmentCopy(assessment);
        } catch (e) {
          dispatch(
            notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: errorFetchMessage(MODULE.ASSESSMENT),
              autoTimeout: true
            })
          );
        }
      })();
    }

    if (!services || !services.length) {
      dispatch(getServices());
    }
  }, [assessmentCopy, dispatch, id, services]);

  return (
    <Box sx={{ height: "calc(100vh - 50px)", overflow: "hidden" }}>
      <Box
        sx={{
          height: "50px",
          px: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <MultipleHeader multipleHeaders={headersInfo} />

        {location?.pathname !== "/assessment/new" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}
          >
            <Can policyAccessKey="opd:changeSettings">
              <OpdSettingsPanel />
            </Can>
            <ReportsPanel module="opd" />
          </Box>
        )}
      </Box>

      {/* routes */}
      <Route
        path={`${match.path}/new`}
        render={(props) => (
          <Assessment
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            assessmentCopy={assessmentCopy}
            setAssessmentCopy={setAssessmentCopy}
          />
        )}
      />

      <Route
        path={`${match.path}/assessments`}
        render={(props) => (
          <AssessmentList
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            setAssessmentCopy={setAssessmentCopy}
          />
        )}
      />

      <Route path={`${match.path}/labTest`} render={() => <PrescribedLabs />} />

      <Route
        path={`${match.path}/labOrderForms`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        render={(props) => <OpdRequests {...props} />}
      />

      <Route
        path={`${match.path}/dentalLabOrders`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        render={(props) => <DentalLabOrders {...props} />}
      />

      <Route path={`${match.path}/glassOrders`} render={() => <GlassOrderList />} />
    </Box>
  );
}

export default AssessmentHome;
